import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { documentApi } from '../../../api/document';

import {
  QButton,
  QFormControl,
  QIcon,
  QRadio,
  QRadioGroup,
  QStack,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getFileDocumentExtension } from '../../../util/FileUploadUtils';
import { BaseDocumentConvertModal } from './BaseDocumentConvertModal';
import { DocumentConvertMessageConfig } from './DocumentConvertMessageConfig';
import { DocumentConvertFormatModalProps } from './types';

enum FileConversionMethod {
  INSERT_AS_ATTACHMENT_LINK = 'insert_as_attachment_link',
  EXTRACT_CONTENT = 'extract_content',
}

const ACCEPTED_EXTRACT_FORMATS = ['docx', 'doc'];

export const ConvertQualioFormat = ({
  onClose,
  qualioDocument,
  onSuccess,
  onError,
}: DocumentConvertFormatModalProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { enhancedMigrations } = useFlags();

  const [isConverting, setIsConverting] = useState(false);
  const [conversionMethod, setConversionMethod] = useState(
    enhancedMigrations
      ? FileConversionMethod.EXTRACT_CONTENT
      : FileConversionMethod.INSERT_AS_ATTACHMENT_LINK,
  );

  const documentConvertFormatMessageConfig = useMemo(() => {
    // Add logic to determine if file is a OneDrive file here
    return DocumentConvertMessageConfig.DocumentUpload;
  }, []);

  const { id: documentId } = qualioDocument;
  const getDocument = async () => documentApi.fetch(documentId.toString());

  const {
    error,
    isLoading,
    isFetching,
    data: latestDocument,
  } = useQuery({
    queryFn: getDocument,
    queryKey: ['latestDocument'],
    refetchOnWindowFocus: false,
  });

  if (error) {
    showToast({
      'data-cy': 'convert-qualio-format-notify-error-toast',
      title: t('notifications.error'),
      description: t('convertFormat.errorLoadingAttachments'),
      status: 'error',
    });
  }

  const isWordFile = Boolean(
    latestDocument &&
      ACCEPTED_EXTRACT_FORMATS.includes(
        getFileDocumentExtension(latestDocument) ?? '',
      ),
  );
  const shouldExtractContent =
    conversionMethod === FileConversionMethod.EXTRACT_CONTENT;
  const isModalLoading = enhancedMigrations && (isLoading || isFetching);
  const isConvertButtonDisabled = isLoading || isModalLoading || isConverting;

  useEffect(() => {
    setConversionMethod(
      enhancedMigrations && isWordFile
        ? FileConversionMethod.EXTRACT_CONTENT
        : FileConversionMethod.INSERT_AS_ATTACHMENT_LINK,
    );
  }, [latestDocument, enhancedMigrations, isWordFile]);

  const handleDocumentConversion = () => {
    if (latestDocument) {
      setIsConverting(true);
      documentApi
        .convertToQualioDocument(latestDocument, shouldExtractContent)
        .then(() => {
          if (onSuccess) {
            onSuccess();
          }
          onClose(true);
        })
        .catch((e) => {
          console.error({
            message: 'Error changing document format',
            e,
          });

          showToast({
            'data-cy': 'convert-qualio-format-notify-error-toast',
            title: t('notifications.error'),
            description: t('convertFormat.couldNotChangeDocumentFormat'),
            status: 'error',
          });

          if (onError) {
            onError();
          }

          onClose(false);
        });
    }
  };

  const modalBody = useMemo(() => {
    if (!isWordFile || !enhancedMigrations) {
      return;
    }

    const handleRadioChange = (value: FileConversionMethod) => {
      setConversionMethod(value);
    };

    return (
      <QFormControl
        id="convert-qualio-format-conversion-method"
        label={t('convertFormat.fileConversionMethod')}
        helper={t('convertFormat.selectFileConversionMethod')}
      >
        <QRadioGroup
          onChange={handleRadioChange}
          value={conversionMethod}
          flexDirection={'column'}
          data-cy="convert-qualio-format-conversion-method-selection"
        >
          <QStack spacing={2}>
            <QRadio
              value={FileConversionMethod.INSERT_AS_ATTACHMENT_LINK}
              data-cy="convert-qualio-format-insert-as-attachment-link"
            >
              <QText>{t('convertFormat.attachFileToFirstSection')}</QText>
            </QRadio>
            <QRadio
              value={FileConversionMethod.EXTRACT_CONTENT}
              data-cy="convert-qualio-format-extract-content"
            >
              <QStack direction={'row'} alignContent={'center'}>
                <QText>{t('convertFormat.extractContentIntoSections')}</QText>
                <QIcon iconName={'QualioAI'} />
              </QStack>
            </QRadio>
          </QStack>
        </QRadioGroup>
      </QFormControl>
    );
  }, [isWordFile, enhancedMigrations, conversionMethod, t]);

  const modalActions = [
    <QButton
      key="convert-qualio-format-cancel-button"
      data-cy="convert-qualio-format-cancel-button"
      variant="ghost"
      isDestructive
      onClick={() => onClose(false)}
    >
      {t('buttons.cancel')}
    </QButton>,
    <QButton
      key="convert-qualio-format-confirm-button"
      data-cy="convert-qualio-format-confirm-button"
      isLoading={isConvertButtonDisabled}
      isDisabled={isConvertButtonDisabled}
      onClick={() => handleDocumentConversion()}
    >
      {t('buttons.confirm')}
    </QButton>,
  ];

  return (
    <BaseDocumentConvertModal
      {...documentConvertFormatMessageConfig}
      isLoading={isModalLoading}
      extendedModalBodyFragment={modalBody}
      modalActionsFragment={modalActions}
      onClose={onClose}
    />
  );
};
